import React, {useState} from "react";
import {Box, Card, Menu, MenuItem, ListItemIcon, ListItemText, TextField} from "@mui/material";
import {IconButton, Typography} from "@mui/joy";
import Tooltip from "@mui/material/Tooltip";
import {
    CheckRounded,
    DeleteRounded,
    DownloadFilledOutlined,
    EditRounded,
    ImageFilledRounded,
    ImageRounded,
    MoreVertRounded,
} from "@mui-symbols-material/w300";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {FileListItem} from "../FileList";
import FileThumbnail from "../FileThumbnail";
import {useFileEntryActions} from "../../hooks/useFileEntryActions";
import {NodeImageTarget} from "../../hooks/useNodeImage";
import {formatBytes} from "../../utils/formatBytes";
import ConfirmDeleteModal from "../ConfirmDeleteModal";

interface FileGridEntryProps {
    file: FileListItem;
    onOpen: (file: FileListItem) => void;
    /** Enables "use as image" for image files of this node. */
    nodeImageTarget?: NodeImageTarget;
}

const FileGridEntry = ({file, onOpen, nodeImageTarget}: FileGridEntryProps) => {
    const {t} = useTranslation();
    const access = file.access;

    const {
        editing,
        setEditing,
        filename,
        setFilename,
        comment,
        setComment,
        handleSaveEdit,
        handleDownload,
        deleteConfirmOpen,
        requestDeleteFile,
        cancelDeleteFile,
        handleDeleteFile,
        deleting,
        fileDisplayName,
        showComment,
        canSetAsNodeImage,
        isCurrentNodeImage,
        handleToggleNodeImage,
        nodeImageActionLabel,
        nodeImageBusy,
    } = useFileEntryActions(file, nodeImageTarget);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(anchorEl);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => setAnchorEl(null);

    const handleOpen = () => {
        if (editing) return;
        onOpen(file);
    };

    if (!access?.read) {
        return null;
    }

    return (
        <Card
            sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "4px",
                boxShadow: "none",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            }}
        >
            {/* Vorschau + Aktionen */}
            <Box sx={{position: "relative"}}>
                <Box
                    onClick={editing ? undefined : handleOpen}
                    sx={{display: "flex", cursor: editing ? "default" : "pointer"}}
                >
                    <FileThumbnail file={file} width="100%" height={160} objectFit="cover" iconSize={72} borderRadius={0} />
                </Box>

                {/* Aktionen oben rechts */}
                <Box sx={{position: "absolute", top: 4, right: 4}}>
                    {editing ? (
                        <IconButton
                            variant="soft"
                            size="sm"
                            onClick={handleSaveEdit}
                            sx={{backgroundColor: "rgba(255,255,255,0.85)"}}
                        >
                            <CheckRounded />
                        </IconButton>
                    ) : (
                        <IconButton
                            variant="soft"
                            size="sm"
                            aria-haspopup="true"
                            aria-expanded={menuOpen ? "true" : undefined}
                            onClick={handleMenuOpen}
                            sx={{backgroundColor: "rgba(255,255,255,0.85)"}}
                        >
                            <MoreVertRounded />
                        </IconButton>
                    )}
                    <Menu
                        anchorEl={anchorEl}
                        open={menuOpen}
                        onClose={handleMenuClose}
                        anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                        transformOrigin={{vertical: "top", horizontal: "right"}}
                    >
                        {canSetAsNodeImage && (
                            <MenuItem
                                disabled={nodeImageBusy}
                                data-testid="file-use-as-image"
                                onClick={() => {
                                    handleToggleNodeImage();
                                    handleMenuClose();
                                }}
                            >
                                <ListItemIcon>
                                    {isCurrentNodeImage
                                        ? <ImageFilledRounded fontSize="small" sx={{color: '#BD4200'}} />
                                        : <ImageRounded fontSize="small" />}
                                </ListItemIcon>
                                <ListItemText>{nodeImageActionLabel}</ListItemText>
                            </MenuItem>
                        )}
                        {access?.write && (
                            <MenuItem
                                onClick={() => {
                                    setEditing(true);
                                    handleMenuClose();
                                }}
                            >
                                <ListItemIcon>
                                    <EditRounded fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>{t("Edit")}</ListItemText>
                            </MenuItem>
                        )}
                        <MenuItem
                            onClick={() => {
                                handleDownload();
                                handleMenuClose();
                            }}
                        >
                            <ListItemIcon>
                                <DownloadFilledOutlined fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>{t("Download")}</ListItemText>
                        </MenuItem>
                        {access?.delete && (
                            <MenuItem
                                onClick={() => {
                                    requestDeleteFile();
                                    handleMenuClose();
                                }}
                            >
                                <ListItemIcon>
                                    <DeleteRounded fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>{t("Delete")}</ListItemText>
                            </MenuItem>
                        )}
                    </Menu>
                </Box>
            </Box>

            {/* Inhalt */}
            <Box sx={{p: "12px", display: "flex", flexDirection: "column", gap: "4px", minWidth: 0}}>
                {editing ? (
                    <Box sx={{display: "flex", flexDirection: "column", gap: "8px"}}>
                        <TextField
                            label={t("File name")}
                            size="small"
                            slotProps={{inputLabel: {shrink: true}}}
                            value={filename}
                            onChange={(e) => setFilename(e.target.value)}
                            fullWidth
                        />
                        {file.supportsComment && (
                            <TextField
                                label={t("Comment")}
                                variant="outlined"
                                size="small"
                                slotProps={{inputLabel: {shrink: true}}}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                fullWidth
                            />
                        )}
                    </Box>
                ) : (
                    <>
                        <Tooltip title={fileDisplayName} placement="bottom-start" arrow={false} enterDelay={1000}>
                            <Typography
                                level="titleSmallLight"
                                sx={{overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
                            >
                                {fileDisplayName}
                            </Typography>
                        </Tooltip>

                        {showComment && (
                            <Tooltip title={file.comment} placement="bottom-start" arrow>
                                <Typography
                                    level="captionBold"
                                    sx={{color: "#666", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
                                >
                                    {file.comment}
                                </Typography>
                            </Tooltip>
                        )}

                        <Box sx={{display: "flex", gap: "8px"}}>
                            <Typography
                                level="captionBold"
                                sx={{color: "#666", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
                            >
                                {file.insert ? moment(file.insert).format("L") : ""}
                            </Typography>
                            <Typography
                                level="captionBold"
                                sx={{color: "#666", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
                            >
                                {formatBytes(file.size)}
                            </Typography>
                        </Box>
                    </>
                )}
            </Box>
            {deleteConfirmOpen && (
                <ConfirmDeleteModal
                    open={deleteConfirmOpen}
                    title={t("Delete file")}
                    message={t("Are you sure you want to delete the file {{name}}? It is removed from this node and cannot be restored.", {name: fileDisplayName})}
                    loading={deleting}
                    onConfirm={handleDeleteFile}
                    onClose={cancelDeleteFile}
                />
            )}
        </Card>
    );
};

export default FileGridEntry;
